import React from 'react';
import {
  CheckSquareOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileZipOutlined,
  FormOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useParams } from 'react-router';
import { Spin, Switch } from 'antd';
import { PRETTY_DATE_TIME_FORMAT } from '@owl-frontend/components';
import type { ClaimFile } from '../../api/files/interface';
import OwlLogo from '../../assets/owl-logo-purple.svg';
import { Locale } from '../../context/AppProvider';
import { globalBus } from '../../context/event-bus';
import { NOTIFICATION_EMIT_EVENTS } from '../../data/notifications/logic';
import useFilesHook from '../ClaimOverview/FilesListContainer/useFilesHook';
import { useEscDossierContext } from '../DossierContext';
import {
  PanelChevron,
  PanelHead,
  PanelLead,
  PanelList,
  PanelPage,
  PanelRow,
  PanelStatus,
} from '../PanelList/PanelList';
import styles from '../PanelList/PanelList.module.scss';
import { authorDisplay, OWL_AUTHOR_KEY } from '../utils';

function noteInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

// eslint-disable-next-line redos/no-vulnerable
const NOTE_TOKEN = /\[([^\]]+)\]\(([^)]*)\)|\[(\d[^\]]*)\]/g;
const isCitationLabel = (label: string): boolean =>
  /^\d[\d\s:,-]*$/.test(label.trim());

function notePreview(raw: string): React.ReactNode {
  const text = raw.replace(/#{1,6}\s+/g, '').replace(/`/g, '');
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  NOTE_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = NOTE_TOKEN.exec(text)) !== null) {
    if (m.index > last) {
      out.push(text.slice(last, m.index));
    }
    const cite = m[3];
    if (cite != null) {
      out.push(<sup key={key++}>[{cite}]</sup>);
    } else if (isCitationLabel(m[1])) {
      out.push(<sup key={key++}>[{m[1]}]</sup>);
    } else {
      out.push(m[1]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    out.push(text.slice(last));
  }
  return out.length > 0 ? out : text;
}

interface Props {
  onFileClick?: (fileId: string) => void;
  kind?: 'files' | 'notes';
}

type LeadKind = 'default' | 'pdf' | 'excel' | 'zip' | 'image';

function leadFor(name: string): { variant: LeadKind; icon: React.ReactNode } {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') {
    return { variant: 'pdf', icon: <FilePdfOutlined /> };
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return { variant: 'excel', icon: <FileExcelOutlined /> };
  }
  if (ext === 'zip') {
    return { variant: 'zip', icon: <FileZipOutlined /> };
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return { variant: 'image', icon: <FileImageOutlined /> };
  }
  return { variant: 'default', icon: <FileTextOutlined /> };
}

const isTaskNote = (f: ClaimFile): boolean => Boolean(f.task_id);

const EscFilesList: React.FC<Props> = ({ onFileClick, kind = 'files' }) => {
  const { messages } = React.useContext(Locale);
  const { dossierId } = useParams<{ dossierId: string }>();
  const { onTaskClick } = useEscDossierContext();
  const [{ files, actions }, handler] = useFilesHook(dossierId ?? '');
  const isNotes = kind === 'notes';
  const [showTaskNotes, setShowTaskNotes] = React.useState(false);

  const fileList = React.useMemo(
    () =>
      Object.values(files)
        .filter((f) =>
          isNotes ? f.file_type === 'ACTIVITY' : f.file_type !== 'ACTIVITY'
        )
        .filter((f) => !isNotes || showTaskNotes || !isTaskNote(f))
        .sort((a, b) => {
          const av = a.received_at ?? a.created_at ?? '';
          const bv = b.received_at ?? b.created_at ?? '';
          return bv.localeCompare(av);
        }),
    [files, isNotes, showTaskNotes]
  );
  const fetchStatus = actions.files.status;
  const loading =
    fetchStatus === 'uninitialized' ||
    (fetchStatus === 'pending' && fileList.length === 0);

  React.useEffect(() => {
    if (!dossierId) {
      return;
    }
    const onTimelineUpdated = (eventDossierId: any) => {
      if (eventDossierId === dossierId) {
        handler.fetchFiles({ stateDb: true });
      }
    };
    const onEventProcessed = (data: any) => {
      if (data?.dossierId === dossierId) {
        handler.fetchFiles({ stateDb: true });
      }
    };
    globalBus.on(NOTIFICATION_EMIT_EVENTS.TimelineUpdated, onTimelineUpdated);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessed, onEventProcessed);
    return () => {
      globalBus.off(
        NOTIFICATION_EMIT_EVENTS.TimelineUpdated,
        onTimelineUpdated
      );
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessed, onEventProcessed);
    };
  }, [dossierId, handler]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  const emptyMsg = isNotes
    ? messages['esc.files.no_notes'] ?? 'No notes yet'
    : messages['esc.files.no_files'] ?? 'No files yet';
  const headTitle = isNotes
    ? messages['esc.tabs.notes'] ?? 'Notes'
    : messages['esc.tabs.files'] ?? 'Files';

  const renderRow = (file: ClaimFile) => {
    const fmt = (d?: string) =>
      d ? dayjs(d).format(PRETTY_DATE_TIME_FORMAT) : undefined;
    const receivedWhen = fmt(file.received_at);
    const createdWhen = fmt(file.created_at);

    if (isNotes) {
      const rawAuthor = file.data?.author as string | undefined;
      const author = rawAuthor ? authorDisplay(rawAuthor) : OWL_AUTHOR_KEY;
      const isOwl = author === OWL_AUTHOR_KEY;
      const authorName = isOwl ? messages['esc.owl_author'] ?? 'Owl' : author;
      return (
        <PanelRow
          key={file.file_id}
          clickable
          onClick={() => onFileClick?.(file.file_id)}
          lead={
            <PanelLead round variant={isOwl ? 'owl' : 'default'}>
              {isOwl ? (
                <OwlLogo width={14} height={14} viewBox="0 0 12 12" />
              ) : (
                noteInitials(authorName)
              )}
            </PanelLead>
          }
          title={authorName}
          desc={
            <>
              {file.task_id && (
                <span
                  role="link"
                  tabIndex={0}
                  className={styles.noteTaskTitle}
                  title="View task"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTaskClick(file.task_id!);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      onTaskClick(file.task_id!);
                    }
                  }}
                >
                  <CheckSquareOutlined />
                  {file.task_title ?? 'View task'}
                </span>
              )}
              {notePreview(file.data?.description ?? 'Activity note')}
            </>
          }
          meta={createdWhen}
          trail={<PanelChevron />}
        />
      );
    }

    const processing =
      file.ingest_status !== 'preprocessed' && file.ingest_status !== 'done';
    const { variant, icon } = leadFor(file.file_name ?? '');
    const meta =
      [
        receivedWhen && `Received ${receivedWhen}`,
        createdWhen && `Added ${createdWhen}`,
      ]
        .filter(Boolean)
        .join('  ·  ') || undefined;
    return (
      <PanelRow
        key={file.file_id}
        clickable={!processing}
        dim={processing}
        onClick={processing ? undefined : () => onFileClick?.(file.file_id)}
        lead={<PanelLead variant={variant}>{icon}</PanelLead>}
        title={file.title ?? file.file_name}
        meta={meta}
        trail={<PanelStatus state={processing ? 'processing' : 'ready'} />}
      />
    );
  };

  return (
    <PanelPage>
      <PanelHead
        icon={isNotes ? <FormOutlined /> : <PaperClipOutlined />}
        title={headTitle}
        count={fileList.length}
        action={
          isNotes ? (
            <label className={styles.headToggle}>
              <span>{messages['esc.files.task_notes'] ?? 'Task notes'}</span>
              <Switch
                size="small"
                checked={showTaskNotes}
                onChange={setShowTaskNotes}
              />
            </label>
          ) : undefined
        }
      />
      {fileList.length === 0 ? (
        <div className={styles.emptyGroup}>{emptyMsg}</div>
      ) : (
        <PanelList>{fileList.map(renderRow)}</PanelList>
      )}
    </PanelPage>
  );
};

export default EscFilesList;
